// Listados de reservas para la administración. Equivale a estas pantallas de la app:
//   autorizar_reservas.dart  → "Pendientes" (aprobar / rechazar con motivo / ver horarios)
//   reservas_aprobadas.dart  → "Autorizadas" (la única que ven guardas y empleados),
//                               "Rechazadas" y "Anuladas" (misma pantalla con tipo 'rechazados' / 'anulados')
//   reservas_todas.dart      → "Todas"
//   ver_reserva.dart / ver_reservas_detalles.dart → detalle con invitados
// Aprobar y rechazar usan las mismas funciones que la app (functions/reservas.js).
import { useState, useEffect, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import BotonSOS from "./sos/boton_sos";
import { cargarSolicitudes, cargarSolicitudesAprobadas, aprobarSolicitud, rechazarSolicitud, obtenerIdConjunto } from "../../functions/reservas";
import { MAX_INVITADOS } from "../../functions/reservas_reglas";
import "bootstrap-icons/font/bootstrap-icons.css";

const PESTANAS = {
  pendientes: { etiqueta: "Pendientes", contador: "Pendientes" },
  aprobadas: { etiqueta: "Autorizadas", contador: "Autorizadas" },
  rechazadas: { etiqueta: "Rechazadas", contador: "Rechazadas" },
  anuladas: { etiqueta: "Anuladas", contador: "Anuladas" },
  todas: { etiqueta: "Todas", contador: "Total" },
};

const columnasDe = (pestana) => [
  { label: "Zona social", key: "zonaId" },
  ...(pestana === "pendientes" ? [] : [{ label: "Sede", key: "_sede" }]),
  { label: "Casa", key: "casa" },
  { label: "Fecha", key: "fecha" },
  { label: "Hora Inicio", key: "horaInicio" },
  { label: "Hora Final", key: "horaFinal" },
  { label: "Total personas", key: "_totalPersonas" },
  ...(pestana === "todas" ? [{ label: "Estado", key: "estado" }] : []),
  ...(pestana === "rechazadas" ? [{ label: "Motivo de rechazo", key: "motivoRechazo" }] : []),
  { label: "Recibo de pago", key: "urlPago" },
];

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.9rem", fontWeight: 500, textDecoration: "none" };
const estiloBoton = { padding: "8px 14px", margin: 2, borderRadius: 8, fontSize: "15px", cursor: "pointer" };

const sedeDe = (r) => {
  const s = String(r.sedeSeleccionada);
  return s === "null" || s === "0" || s === "undefined" ? "1" : s;
};
const ordenarPorFechaDesc = (lista) => lista.sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)));

const verDetalle = (r) => {
  const invitados = [];
  for (let i = 1; i <= Math.min(r.cantidadInvitados ?? 0, MAX_INVITADOS); i++) {
    invitados.push(`<p style="margin:10px 0 2px;font-weight:700">Invitado ${i}:</p>
      <p style="margin:0">${r[`nombreInvitado${i}`] ?? ""}</p>
      <p style="margin:0">${r[`tipoDocumentoInvitado${i}`] ?? ""}: ${r[`numeroDocumentoInvitado${i}`] ?? ""}</p>`);
  }
  const fila = (t, v) => `<p style="margin:6px 0"><strong>${t}</strong>  ${v ?? ""}</p>`;
  Swal.fire({
    title: r.zona ?? r.zonaId ?? "Solicitud",
    html: `<div style="font-size:16px">
      ${fila("Casa:", r.casa)}${fila("Titular:", r.titular)}${fila("Fecha:", r.fecha)}
      ${fila("Hora Inicio:", r.horaInicio)}${fila("HoraFinal:", r.horaFinal)}${fila("Total Personas:", (r.cantidadInvitados ?? 0) + 1)}
      ${invitados.length ? `<hr/><p style="font-weight:700;font-size:17px;margin:6px 0">Invitados</p>${invitados.join("")}` : ""}
    </div>`,
    confirmButtonText: "Cerrar", confirmButtonColor: "#c0392b",
  });
};

const ListadoReservas = memo(() => {
  const navigate = useNavigate();
  const permisos = localStorage.getItem("Permisos") || "";
  const codigo = localStorage.getItem("CodigoCliente") || "";
  const esAdmin = permisos === "ADMINISTRADOR" || permisos === "SUPER";

  const [pestana, setPestana] = useState(esAdmin ? "pendientes" : "aprobadas");
  const [datos, setDatos] = useState([]);
  const [zona, setZona] = useState("Todos");
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      let lista;
      if (pestana === "pendientes") {
        lista = (await cargarSolicitudes(codigo)).filter(r => r.casa !== undefined && r.casa !== null);
      } else if (pestana === "aprobadas") {
        lista = ordenarPorFechaDesc(await cargarSolicitudesAprobadas(codigo, "todo", "aprobados"));
      } else if (pestana === "rechazadas") {
        lista = ordenarPorFechaDesc(await cargarSolicitudesAprobadas(codigo, "todo", "rechazados"));
      } else if (pestana === "anuladas") {
        // Una reserva anulada queda en la carpeta donde estaba (aprobados o pendientes).
        const [a, p] = await Promise.all(["aprobados", "pendientes"].map(e => cargarSolicitudesAprobadas(codigo, "todo", e)));
        lista = ordenarPorFechaDesc([...a, ...p].filter(r => r.estado === "Anulado"));
      } else {
        const [a, p, r] = await Promise.all(["aprobados", "pendientes", "rechazados"].map(e => cargarSolicitudesAprobadas(codigo, "todo", e)));
        lista = ordenarPorFechaDesc([...a, ...p, ...r]);
      }
      if (pestana !== "pendientes") lista = lista.filter(r => r.zonaId !== undefined && r.zonaId !== null);
      setDatos(lista.map(r => ({ ...r, _sede: sedeDe(r), _totalPersonas: (r.cantidadInvitados ?? 0) + 1 })));
    } catch (e) {
      console.error(e);
      setDatos([]);
    }
    setZona("Todos");
    setCargando(false);
  }, [pestana, codigo]);

  useEffect(() => { cargar(); }, [cargar]);

  const aprobar = async (fila) => {
    setCargando(true);
    const idConjunto = await obtenerIdConjunto(codigo);
    const ok = await aprobarSolicitud({ codigo, idConjunto, reserva: { ...fila } });
    await Swal.fire({ text: ok ? "La reserva fue aprobada" : "No se pudo aprobar la reserva", confirmButtonColor: "#c0392b" });
    cargar();
  };

  const rechazar = async (fila) => {
    const { value: motivo } = await Swal.fire({
      title: "Informe al residente la causa del rechazo",
      input: "textarea",
      inputLabel: "Motivo de rechazo",
      inputValue: fila.motivoRechazo ?? "",
      showCancelButton: true, confirmButtonText: "Confirmar", cancelButtonText: "Cancelar", confirmButtonColor: "#961819",
      inputValidator: (v) => ((v ?? "").length < 3 ? "Ingrese Motivo de rechazo" : undefined),
    });
    if (motivo === undefined) return;
    setCargando(true);
    const idConjunto = await obtenerIdConjunto(codigo);
    const ok = await rechazarSolicitud({ codigo, idConjunto, reserva: { ...fila }, motivo });
    await Swal.fire({ text: ok ? "La reserva fue rechazada" : "No se pudo rechazar la reserva", confirmButtonColor: "#c0392b" });
    cargar();
  };

  const verHorarios = (fila) => navigate("/crear_reserva", { state: { zonaId: fila.zonaId } });

  const botones = [
    { texto: "Ver", icono: "bi-eye", accion: verDetalle, style: estiloBoton },
    ...(pestana === "pendientes" ? [
      { texto: "Aprobar", icono: "bi-check-circle", accion: aprobar, style: { ...estiloBoton, background: "#16a34a", color: "#fff", border: "none" } },
      { texto: "Rechazar", icono: "bi-x-circle", accion: rechazar, style: { ...estiloBoton, background: "#dc2626", color: "#fff", border: "none" } },
      { texto: "Horarios", icono: "bi-calendar3", accion: verHorarios, style: estiloBoton },
    ] : []),
  ];

  const zonas = ["Todos", ...[...new Set(datos.map(r => String(r.zonaId)))].sort()];
  const filtrados = zona === "Todos" ? datos : datos.filter(r => String(r.zonaId) === zona);
  const columnas = columnasDe(pestana);
  const pestanasVisibles = esAdmin ? Object.keys(PESTANAS) : ["aprobadas"];

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "#f5f6fa", padding: "20px 28px 4px" }}>
        {pestanasVisibles.map(p => (
          <button key={p} onClick={() => setPestana(p)}
            style={{ padding: "10px 18px", borderRadius: 20, border: "none", fontWeight: 700, fontSize: 16, cursor: "pointer", background: pestana === p ? "#c0392b" : "#f0f0f0", color: pestana === p ? "#fff" : "#000" }}>
            {PESTANAS[p].etiqueta}
          </button>
        ))}
      </div>
      {cargando ? <AnimacionCarga /> : (
        <TablaGenerica
          datos={filtrados}
          titulos={columnas.map(c => c.label.toUpperCase())}
          campos={columnas.map(c => c.key)}
          elementosPorPagina={3000}
          renderizadores={{
            urlPago: (url) => url ? <a href={url} target="_blank" rel="noreferrer">Ver recibo de pago</a> : "",
          }}
          contenidoExtra={
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", margin: "12px 0" }}>
              <label style={{ fontWeight: 700, fontSize: 16 }}>Filtrar zona social:</label>
              <select value={zona} onChange={e => setZona(e.target.value)} style={{ padding: "8px 12px", fontSize: 16, borderRadius: 8 }}>
                {zonas.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
              <span style={{ fontWeight: 700, fontSize: 18 }}>{PESTANAS[pestana].contador}: <span style={{ color: pestana === "pendientes" ? "orange" : "inherit" }}>{filtrados.length}</span></span>
              <div style={{ marginLeft: "auto", display: "flex" }}>
                <button onClick={cargar} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
                <CSVLink data={filtrados} headers={columnas} filename="listado_reservas.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
              </div>
            </div>
          }
          botones={botones}
        />
      )}
      <BotonSOS />
    </>
  );
});

export default ListadoReservas;
